import { useMemo } from "react";

import type { WorkspaceNode, WorkspaceNodeType } from "../../../types/workspace";

export type WorkspaceEntityTitlesByType = {
  report: Record<string, string>;
  strategy: Record<string, string>;
  simulation: Record<string, string>;
  policy: Record<string, string>;
};

const EMPTY_TITLES: WorkspaceEntityTitlesByType = {
  report: {},
  strategy: {},
  simulation: {},
  policy: {},
};

/** Generic labels that mean "we don't have a real title yet". */
const GENERIC_TITLES = new Set([
  "strategy",
  "report",
  "simulation",
  "policy",
  "untitled",
  "new report",
]);

function isUsableTitle(title: string | null | undefined): boolean {
  const trimmed = title?.trim() ?? "";
  if (!trimmed) return false;
  return !GENERIC_TITLES.has(trimmed.toLowerCase());
}

function titleBucket(type: WorkspaceNodeType): keyof WorkspaceEntityTitlesByType | null {
  if (type === "report") return "report";
  if (type === "strategy") return "strategy";
  if (type === "simulation" || type === "combined_simulation") return "simulation";
  if (type === "policy" || type === "combined_policy") return "policy";
  return null;
}

function titlesFromNodeMeta(nodes: WorkspaceNode[] | undefined): WorkspaceEntityTitlesByType {
  const next: WorkspaceEntityTitlesByType = {
    report: {},
    strategy: {},
    simulation: {},
    policy: {},
  };
  for (const node of nodes ?? []) {
    const bucket = titleBucket(node.type);
    if (!bucket) continue;
    const title = node.meta?.title?.trim();
    if (!isUsableTitle(title)) continue;
    next[bucket][node.entityId] = title!;
  }
  return next;
}

/**
 * Resolves canvas card titles from embedded workspace `node.meta` only.
 * Missing titles stay empty — do not fan out entity GETs on canvas open.
 */
export function useWorkspaceEntityTitles(
  nodes: WorkspaceNode[] | undefined
): WorkspaceEntityTitlesByType {
  return useMemo(() => titlesFromNodeMeta(nodes), [nodes]);
}

/** @deprecated Prefer useWorkspaceEntityTitles */
export function useWorkspaceReportTitles(
  nodes: WorkspaceNode[] | undefined
): Record<string, string> {
  return useWorkspaceEntityTitles(nodes).report;
}

export { EMPTY_TITLES, isUsableTitle };
